/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useCallback, useState } from "react"

import { createEvacPostBodyFormData } from "./postBodyGenerators"
import { createEvdcPostBodyFromData } from "../../../../../components/devices/lct/formSpecs/evdcSpec"
import { createHpPostBodyFromData } from "../../../../../components/devices/lct/formSpecs/hpSpec"
import {
    EVACDeviceFormData,
    EVDCDeviceFormData,
    HpDeviceFormData,
    isLctFormDataEvac,
    isLctFormDataHp,
} from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import { publishLCTDeviceMapper } from "../../../mappers/registerDevice/publishLCTDeviceMapper"
import { TPublishLCTDeviceResponse } from "../../../types/devices/lct/publishLctGeneralTypes"
import { PUBLISH_LCT_DEVICE } from "../../../urls/private"

interface IPublisLCTDeviceOutput {
    publishLCTDevice: () => Promise<TPublishLCTDeviceResponse>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

const usePublishLCTDevice = (
    formData: EVACDeviceFormData | EVDCDeviceFormData | HpDeviceFormData,
    dataset: LCTOnlyDatasets,
    attachmentUUID?: string,
    systemReference?: string
): IPublisLCTDeviceOutput => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    const publishLCTDevice =
        useCallback(async (): Promise<TPublishLCTDeviceResponse> => {
            let response: TPublishLCTDeviceResponse = {
                deviceRef: "",
            }
            if (!attachmentUUID) {
                return response
            }

            let postBody = {}
            if (isLctFormDataEvac(formData, dataset)) {
                postBody = createEvacPostBodyFormData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else if (isLctFormDataHp(formData, dataset)) {
                postBody = createHpPostBodyFromData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else {
                postBody = createEvdcPostBodyFromData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            }

            try {
                setIsLoading(true)
                const postResponse = await postClientProtected(
                    PUBLISH_LCT_DEVICE,
                    postBody
                )
                response = publishLCTDeviceMapper.map(postResponse)
                snackbar("Successfully published device", "success")
                setIsLoaded(true)
            } catch {
                const appError = invalidApiDataError("Couldn't publish Device")
                setError(appError)
                snackbar(
                    `Couldn't publish device, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
            } finally {
                setIsLoading(false)
            }
            return response
        }, [attachmentUUID, formData, dataset])

    return {
        publishLCTDevice,
        isLoading,
        isLoaded,
        error,
    }
}

export default usePublishLCTDevice
